export type CopilotWorkspace = "research" | "trading" | "publishing";
export type Workspace = CopilotWorkspace | "market" | "shared";

const TRADING_PATHS = ["/positions", "/broker-trading", "/grid", "/swing", "/t-recap", "/risk", "/journal", "/quant-agents"];
const RESEARCH_PATHS = ["/lab", "/ml", "/auto-tune", "/indicators", "/rps/backtest", "/screen-runs", "/ask", "/grid/research"];
const matches = (path: string, prefix: string) => path === prefix || path.startsWith(`${prefix}/`);

export function workspaceForLocation(path: string, search = ""): Workspace {
  if (path === "/ml/publishing") return "publishing";
  if (path === "/copilot") {
    const selected = new URLSearchParams(search).get("workspace");
    return selected === "trading" || selected === "publishing" ? selected : "research";
  }
  if (RESEARCH_PATHS.some(prefix => matches(path, prefix))) return "research";
  if (TRADING_PATHS.some(prefix => matches(path, prefix))) return "trading";
  return path.startsWith("/settings") || path === "/library" ? "shared" : "market";
}

/** Account changes only remount pages that actually operate on that account. */
export function workspaceMountKey(userId: number | undefined, workspace: Workspace, environment: string, accountId?: number | null): string {
  return workspace === "trading" ? `${userId}:trading:${environment}:${accountId ?? "none"}` : `${userId}:independent`;
}

export function copilotScopeForLocation(path: string, search = ""): CopilotWorkspace {
  const workspace = workspaceForLocation(path, search);
  return workspace === "trading" || workspace === "publishing" ? workspace : "research";
}

export function navigationForWorkspace<T extends { id: string }>(groups: T[], workspace: Workspace): T[] {
  if (workspace !== "research" && workspace !== "trading") return groups;
  const active = `${workspace}-workspace`;
  const inactive = workspace === "research" ? "trading-workspace" : "research-workspace";
  const visible = groups.filter(group => group.id !== inactive);
  const index = visible.findIndex(group => group.id === active);
  if (index > 1) visible.splice(1, 0, visible.splice(index, 1)[0]!);
  return visible;
}

export function copilotWorkspaceContext(
  context: { page_id: string; path: string; state: Record<string, unknown> },
  workspace: Workspace,
  environment: string,
  account: { id: number; name: string; is_paper: boolean } | null,
) {
  const state = { ...context.state };
  for (const key of ["account_id", "account_name", "is_paper", "trading_environment"]) delete state[key];
  return {
    ...context,
    state: {
      ...state,
      workspace: workspace === "trading" || workspace === "publishing" ? workspace : "research",
      ...(workspace === "trading" ? {
        trading_environment: environment, account_id: account?.id ?? null,
        account_name: account?.name ?? null, is_paper: account?.is_paper ?? null,
      } : {}),
    },
  };
}
